import { useEnabledFeatures } from '../../common/hooks/enabled-features/use-enabled-features';
import { ClassificationStrategy, useLocationApi } from '../hooks/use-location-api';
import { useFieldIdentifier } from '../../forms/field-identifier';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { LocationStoreView } from '../types/locations';
import { mapConsolidatedLocationViewToLocationStoreView } from '../helpers/data-mapping';
import { LocationStore } from '../location-store';
import composeRefs from '@seznam/compose-react-refs';
import { takeLast } from '../../common/helpers/take-last-promise';
import { useKeyboardNavigation } from '../hooks/use-keyboard-selection';
import { debounce } from 'lodash';
import { ToggleOption } from '../../common/components/toggle-group';
import {
    ActionHandler,
    useIntegratedActionBroadcaster,
} from '../../action-broadcaster/use-integrated-action-broadcaster';
import { LocationDisplayProps } from './location-map/types';
import { ConsolidatedLocationView } from '@mark43/cad-api';
import {
    getForcedResultFromQuery,
    removeSubpremisesQueryFromAddress,
} from '../helpers/forced-results';
import { getLocationSummaryFirstLineText } from '../helpers/location-summary';
import { getSubpremisesQueryFromLocation } from '../helpers/location-display';
import { useApplicationType } from '../../common/hooks/use-application-type';
import { reformatMaritimeCoordinates } from '../../location/helpers';
import { logDebug } from '../../monitoring/log';

type LocationSearchViewMode = 'LIST' | 'MAP' | 'COMPACT';

interface LocationSelectStateProps {
    onChange?: (view: LocationStoreView | null, didBlur?: boolean) => void;
    value: LocationStoreView | ConsolidatedLocationView | null;
    classificationStrategy?: ClassificationStrategy;
    moveFocusNext?: () => void;
    ref: React.ForwardedRef<object>;
}

export function useLocationSelectState(props: LocationSelectStateProps) {
    const { onChange, value, classificationStrategy = 'SINGLE_AGENCY', moveFocusNext, ref } = props;
    const { useMapboxMapForLocationInputs, subpremiseSupport, maritimeCoordinatesEnabled } =
        useEnabledFeatures();

    const {
        searchLocations,
        isLoadingResults,
        isClassifyingLocation,
        resolveEntityLinksForLocation,
    } = useLocationApi(classificationStrategy);
    const fieldIdentifier = useFieldIdentifier();
    const [isPopoverOpen, setIsPopoverOpen] = useState(false);

    const closePopover = React.useCallback(
        function closePopover(): void {
            setIsPopoverOpen(false);
        },
        [setIsPopoverOpen]
    );
    const wrappedOnChange = React.useCallback(
        async (value: LocationStoreView | null) => {
            const finalValue = value ? removeSubpremisesQueryFromAddress(value) : null;
            if (!finalValue) {
                onChange?.(null);
                closePopover();
                return;
            }

            const submit = (view: LocationStoreView | null): void => {
                onChange?.(view);
                closePopover();
            };

            if (finalValue.areCoordinatesVerified === false) {
                submit(finalValue);
                return;
            }

            if (!finalValue.streetAddress && finalValue.type !== 'POINT') {
                submit(null);
                return;
            }

            try {
                const resolved = await resolveEntityLinksForLocation(finalValue);
                const mappedValue = mapConsolidatedLocationViewToLocationStoreView(resolved);
                submit(mappedValue);
            } catch {
                submit(finalValue);
            }
        },
        [onChange, resolveEntityLinksForLocation, closePopover]
    );
    const store = useState(new LocationStore(value, wrappedOnChange))[0];
    const [query, setQuery] = useState('');
    const [currentMode, setCurrentMode] = useState<LocationSearchViewMode>('LIST');

    const wrapperRef = useRef<HTMLElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const composedRef = composeRefs(inputRef, ref);
    const ensureLast = useRef(takeLast<LocationStoreView[]>());

    const [hasSyncedSubpremisesToQuery, setHasSyncedSubpremisesToQuery] = useState(false);

    const { isFirstResponder } = useApplicationType();
    const size = isFirstResponder ? 'lg' : 'md';

    useEffect(
        function synchronizeLocationValueToQuery() {
            if (value) {
                const view = mapConsolidatedLocationViewToLocationStoreView(value);
                const newQuery = getLocationSummaryFirstLineText(view);
                // To avoid issues with losing cursor positioning when editing from the middle of the
                // input, we have to synchronously set the value of query as a user types (https://dev.to/kwirke/solving-caret-jumping-in-react-inputs-36ic)
                // However, we still want to in general always be synchronizing a location select's value to it's query. This effect guarantees that the values
                // will synchronize but does not change the query if this would be redundant (which would cause the cursor to position at the end after a single character change).
                if (query !== newQuery) {
                    if (
                        (hasSyncedSubpremisesToQuery && !query.includes('#')) ||
                        view.isForcedResult || // Forced results have the subpremise query baked into the street address.
                        !subpremiseSupport
                    ) {
                        setQuery(newQuery);
                    } else {
                        setQuery(newQuery + ' ' + getSubpremisesQueryFromLocation(view));
                        setHasSyncedSubpremisesToQuery(true);
                    }
                }
            } else {
                setQuery('');
            }
        },
        [value, query, isPopoverOpen, subpremiseSupport, hasSyncedSubpremisesToQuery]
    );

    const onKeyboardSelectsLocation = useCallback(() => {
        logDebug('[FOCUS DEBUG] location-select onKeyboardSelectsLocation calling inputRef.focus()');
        inputRef?.current?.focus?.();
        moveFocusNext?.();
    }, [moveFocusNext]);

    useKeyboardNavigation(wrapperRef, store, onKeyboardSelectsLocation);

    // Must have at least one result and it cannot be the forced location.
    const isMapDisabled =
        store.data.list.length === 0 ||
        (store.data.list.length === 1 && store.data.list?.[0]?.isForcedResult);

    const openPopover = React.useCallback(
        function openPopover(): void {
            setIsPopoverOpen(true);
            store.setSort(undefined);
        },
        [setIsPopoverOpen, store]
    );

    const debouncedSearch = useMemo(() => {
        async function onSearch(query: string): Promise<void> {
            if (query === '') {
                store.setItems([]);
            } else {
                const newResults = await ensureLast.current(searchLocations(query));
                store.setItems(newResults);
            }
        }

        return debounce(onSearch, 300);
    }, [searchLocations, store]);

    function onToggle(item: ToggleOption): void {
        setCurrentMode(item.label as LocationSearchViewMode);
        store.setSort(undefined);
    }

    const handleActions: ActionHandler<'EVENT_V2'> = useCallback(
        (action) => {
            switch (action.type) {
                case 'SET_LOCATION_QUERY':
                    if (fieldIdentifier && action.payload.fieldIdentifier === fieldIdentifier) {
                        const query = action.payload.query;
                        if (query) {
                            const forcedResult = getForcedResultFromQuery(query);
                            onChange?.(forcedResult);
                        } else {
                            onChange?.(null);
                        }
                        debouncedSearch(query);
                        openPopover();
                        logDebug(
                            `[FOCUS DEBUG] location-select action broadcaster calling inputRef.focus() for field: ${fieldIdentifier}`
                        );
                        inputRef.current?.focus();
                        return true;
                    }
                    break;
                case 'SUBMIT_LOCATION_VIEW':
                    if (fieldIdentifier && action.payload.fieldIdentifier === fieldIdentifier) {
                        const { location } = action.payload;

                        onChange?.(location);
                        closePopover();
                        return true;
                    }
                    break;
            }
        },
        [debouncedSearch, fieldIdentifier, onChange, openPopover, closePopover]
    );

    useIntegratedActionBroadcaster({
        id: `LOCATION_SELECT`,
        windowType: 'EVENT_V2',
        handler: handleActions,
    });

    const onInputChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const inputValue = e.target.value;
            if (!isPopoverOpen) {
                openPopover();
            }
            if (!inputValue) {
                onChange?.(null);
                setQuery('');
                debouncedSearch(inputValue);
            } else {
                const forcedLocation = getForcedResultFromQuery(inputValue);
                onChange?.(forcedLocation);
                setQuery(getLocationSummaryFirstLineText(forcedLocation));
                const valueToSend = maritimeCoordinatesEnabled
                    ? reformatMaritimeCoordinates(inputValue)
                    : inputValue;
                debouncedSearch(valueToSend);
            }
        },
        [debouncedSearch, isPopoverOpen, onChange, openPopover, maritimeCoordinatesEnabled]
    );

    const locationDisplayProps: LocationDisplayProps = {
        inputRef,
        moveFocusNext,
        store,
        isLoadingResults: isLoadingResults || isClassifyingLocation,
        query,
        closePopover: () => {
            setIsPopoverOpen(false);
        },
        size,
    };

    const resultCountWithoutForcedLocations =
        store.data.list.length -
        store.data.list.filter((location) => location.isForcedResult).length;

    return {
        wrapperRef,
        inputRef,
        composedRef,
        isPopoverOpen,
        openPopover,
        closePopover,
        currentMode,
        isMapDisabled,
        onToggle,
        onInputChange,
        locationDisplayProps,
        resultCountWithoutForcedLocations,
        useMapboxMapForLocationInputs,
        query,
    };
}
